import * as React from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { makeTheme } from "./theme";

type Mode = "light" | "dark";

type Ctx = {
    mode: Mode;
    toggle: () => void;
    setMode: (m: Mode) => void;
};

const ThemeModeCtx = React.createContext<Ctx | null>(null);

export function useThemeMode() {
    const v = React.useContext(ThemeModeCtx);
    if (!v) throw new Error("useThemeMode must be used within ThemeModeProvider");
    return v;
}

export default function ThemeModeProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [mode, setMode] = React.useState<Mode>(() => {
        const saved = localStorage.getItem("rb_theme_mode");
        return (saved === "dark" || saved === "light") ? saved : "light";
    });

    const toggle = React.useCallback(() => {
        setMode((m) => (m === "light" ? "dark" : "light"));
    }, []);

    React.useEffect(() => {
        localStorage.setItem("rb_theme_mode", mode);
    }, [mode]);

    const theme = React.useMemo(() => makeTheme(mode), [mode]);

    const value = React.useMemo(() => ({ mode, toggle, setMode }), [mode, toggle]);

    return (
        <ThemeModeCtx.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeModeCtx.Provider>
    );
}

export { };
